// react
import { Suspense } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useParams } from '@tanstack/react-router';

// material ui
import { Box, Card, Grid, Typography } from '@mui/material';

// api
import { useGetEnergyOverviewDataQuery } from '@/api/energy-analysis/calculateEnergySavingsApiSlice';
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';
import { useGetLocationByIdQuery } from '@/api/recalcV2/endpoints/locationsApiSlice';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-components
import EnergyXplorerAlarmManagementActions from '@/features/energyxplorer/components/alarms/EnergyXplorerAlarmManagementActions';
import ConsumptionCard from '@/features/energyxplorer/components/card/ConsumptionCard';
import OpeningHoursCard from '@/features/energyxplorer/components/card/OpeningHoursCard';
import EnergyCoverageCard from '@/features/energyxplorer/components/EnergyCoverageCard';
import InfrastructureNodeDataGrid from '@/features/energyxplorer/components/nodes/InfrastructureNodeDataGrid';
import SeuNodeDataGrid from '@/features/energyxplorer/components/nodes/SeuNodeDataGrid';
import EnergyXplorerAlarmSetupCard from '@/features/energyxplorer/components/portfolio-dashboard/EnergyXplorerAlarmSetupCard';
import EnergyXplorerAlarmsGrid from '@/features/energyxplorer/components/portfolio-dashboard/EnergyXplorerAlarmsGrid';

// feature-hooks
import { useDeepDiveAlarms } from '@/features/energyxplorer/hooks/useDeepDiveAlarms';
import { useEnergyXplorerAlarmSetup } from '@/features/energyxplorer/hooks/useEnergyXplorerAlarmSetup';

// components
import { useHasService } from '@/hooks/useHasService';
import { useUrlDateRange } from '@/hooks/useUrlDateRange';

export const Route = createFileRoute(
  '/$accountId/_user/energyxplorer/deepdive/$energyGroupId/consumption-overview',
)({
  component: RouteComponent,
  staticData: { crumb: 'energyxplorer:app_bar.consumption_overview' },
});

function RouteComponent() {
  const { t } = useTranslation([
    'energyxplorer-energy-overview-tab',
    'energyxplorer',
  ]);

  const { timeRange } = useUrlDateRange();

  const { accountId, energyGroupId } = useParams({ strict: false });
  const numericAccountId = accountId ? Number(accountId) : undefined;

  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    {
      skip: !energyGroupId,
    },
  );

  const { data: locationData } = useGetLocationByIdQuery(
    {
      locationId: selectedEnergyGroup?.locationId,
      accountId: accountId,
    },
    {
      skip: !selectedEnergyGroup?.locationId || !accountId,
    },
  );

  const {
    data: energyOverviewDataResult,
    isLoading: isLoadingEnergyOverview,
    isFetching: isFetchingEnergyOverview,
    error: energyOverviewError,
  } = useGetEnergyOverviewDataQuery(
    {
      accountId: accountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      start: timeRange?.start,
      end: timeRange?.end,
    },
    {
      skip:
        !accountId ||
        !selectedEnergyGroup?.energyGroupId ||
        !timeRange.start ||
        !timeRange.end,
    },
  );

  const { hasService: hasAnalyticsService, isLoading: isAnalyticsLoading } =
    useHasService('EnergyXplorerAnalytics') as unknown as {
      hasService: boolean;
      isLoading: boolean;
    };

  const { alarms, isLoading: isAlarmsLoading } = useDeepDiveAlarms({
    energyGroups: selectedEnergyGroup ? [selectedEnergyGroup] : undefined,
    accountId: numericAccountId,
    enabled: hasAnalyticsService,
  });

  const {
    shouldShowSetupPrompt,
    isLoading: isAlarmSetupLoading,
    isEnabling,
    enableAlarms,
    autoCreatedAlarmGroup,
    canDeleteAutoCreatedAlarmGroup,
    isDeletingAutoCreatedAlarmGroup,
    deleteAutoCreatedAlarmGroup,
  } = useEnergyXplorerAlarmSetup({
    accountId: numericAccountId,
    enabled: hasAnalyticsService,
  });

  const openingHours =
    selectedEnergyGroup?.energyTreeNodes[0]?.options?.openingHours || null;

  return (
    <PageContainer sx={{ pt: 0 }}>
      <GatewayLoadingIndicator
        isLoading={isLoadingEnergyOverview || isFetchingEnergyOverview}
      />

      {energyOverviewError && (
        <Box
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            mb: 2,
          }}
        >
          {/* SVG Icon for Missing Data */}
          <svg
            width="80"
            height="80"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ margin: '0 auto 16px auto', opacity: 0.6 }}
          >
            {!energyOverviewDataResult && (
              <path
                d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"
                fill="#9e9e9e"
              />
            )}
          </svg>

          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('data_unavailable_overlay.title')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 'auto' }}
          >
            {t('data_unavailable_overlay.description')}
          </Typography>
        </Box>
      )}
      {energyOverviewDataResult &&
        !energyOverviewError &&
        selectedEnergyGroup?.energyTreeNodes.length > 0 && (
          <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                <Grid size={12}>
                  <ConsumptionCard
                    energyDataSummary={
                      energyOverviewDataResult.EnergyDataSummary
                    }
                    dataPoints={energyOverviewDataResult.EnergyData}
                  />
                </Grid>
                <Grid size={12}>
                  <EnergyCoverageCard
                    title={t('energy_coverage_card.title')}
                    description={t('energy_coverage_card.description')}
                    energyOverviewDataResult={energyOverviewDataResult}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 12,
                md: 6,
              }}
            >
              <Suspense
                fallback={
                  <div>{t('analysis.loading', { ns: 'energyxplorer' })}</div>
                }
              >
                {openingHours && (
                  <OpeningHoursCard
                    title={t('common:tooltips.information')}
                    description={t('energy_group_info_card.description')}
                    location={locationData}
                    openingHours={openingHours}
                  />
                )}
              </Suspense>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 12,
                md: 12,
              }}
            >
              {energyOverviewDataResult.DistributionBoardsSummary.length > 0 ? (
                <InfrastructureNodeDataGrid
                  energyOverviewDataResult={energyOverviewDataResult}
                />
              ) : (
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('analysis.no_panels', { ns: 'energyxplorer' })}
                  </Typography>
                </Card>
              )}
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 12,
                md: 12,
              }}
            >
              {energyOverviewDataResult.EnergyDistributionSummary.length > 2 ? (
                <SeuNodeDataGrid
                  energyOverviewDataResult={energyOverviewDataResult}
                  distributionBoardSummary={
                    energyOverviewDataResult.DistributionBoardsSummary
                  }
                  energyDistributionSummary={
                    energyOverviewDataResult.EnergyDistributionSummary
                  }
                />
              ) : (
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('analysis.no_consumers', { ns: 'energyxplorer' })}
                  </Typography>
                </Card>
              )}
            </Grid>
          </Grid>
        )}

      {selectedEnergyGroup && hasAnalyticsService && !isAnalyticsLoading && (
        <Box sx={{ mt: 2 }}>
          {shouldShowSetupPrompt ? (
            <EnergyXplorerAlarmSetupCard
              accountId={accountId}
              title={t('deepdive.alarms_list.title', {
                ns: 'energy-analysis',
              })}
              onEnable={enableAlarms}
              isLoading={isEnabling}
              autoCreatedAlarmGroupName={autoCreatedAlarmGroup?.Name}
              canDeleteAutoCreatedAlarmGroup={canDeleteAutoCreatedAlarmGroup}
              isDeletingAutoCreatedAlarmGroup={isDeletingAutoCreatedAlarmGroup}
              onDeleteAutoCreatedAlarmGroup={deleteAutoCreatedAlarmGroup}
            />
          ) : (
            <EnergyXplorerAlarmsGrid
              accountId={accountId}
              alarms={alarms}
              isLoading={isAlarmsLoading || isAlarmSetupLoading}
              emptyText={t('deepdive.alarms_list.empty_single', {
                ns: 'energy-analysis',
              })}
              title={t('deepdive.alarms_list.title', {
                ns: 'energy-analysis',
              })}
              toolbarActions={
                <EnergyXplorerAlarmManagementActions
                  accountId={accountId}
                  autoCreatedAlarmGroupName={autoCreatedAlarmGroup?.Name}
                  canDeleteAutoCreatedAlarmGroup={
                    canDeleteAutoCreatedAlarmGroup
                  }
                  isDeletingAutoCreatedAlarmGroup={
                    isDeletingAutoCreatedAlarmGroup
                  }
                  onDeleteAutoCreatedAlarmGroup={deleteAutoCreatedAlarmGroup}
                />
              }
            />
          )}
        </Box>
      )}
    </PageContainer>
  );
}
